import { Controller } from 'stimulus'

// Mounted ONCE on the tree wrapper (Stimulus 1.x). Handles, scoped to
// `this.element`: per-branch fold/unfold (event bubbles from each toggle),
// expand/collapse all, search-in-tree (highlight + auto-expand ancestors),
// and persistence of the fold state to localStorage.
export default class extends Controller {

  static targets = ["search"]

  connect() {
    this.restore()
  }

  get storageKey() {
    return this.element.dataset.storageKey
  }

  // ── Per-branch fold / unfold ──────────────────────────────
  toggleBranch(event) {
    event.preventDefault()
    const li = event.currentTarget.closest("li")
    if (!li) return
    const branch = this.branchFor(li)
    if (!branch) return

    const collapsed = branch.classList.toggle("d-none")
    this.syncIcons(li, collapsed)
    this.persist()
  }

  branchFor(li) {
    return Array.from(li.parentElement.children).find(el => el.matches("[data-tree-branch]"))
  }

  rowForBranch(branch) {
    return Array.from(branch.parentElement.children).find(el => el.matches("li[data-tree-row]"))
  }

  syncIcons(li, collapsed) {
    li.querySelectorAll("[data-tree-icon]").forEach(icon => {
      const isPlus = icon.dataset.treeIcon === "plus"
      icon.classList.toggle("d-none", isPlus ? !collapsed : collapsed)
    })
  }

  // ── Search in tree ────────────────────────────────────────
  search() {
    const query = (this.hasSearchTarget ? this.searchTarget.value : "").trim().toLowerCase()

    this.rows().forEach(li => {
      const label = li.querySelector("[data-tree-label]")
      if (!query) {
        li.classList.remove("tree-dim")
        if (label) label.classList.remove("tree-search-hit")
        return
      }
      const text = (li.dataset.treeText || label?.textContent || "").toLowerCase()
      const hit  = text.includes(query)
      li.classList.toggle("tree-dim", !hit)
      if (label) label.classList.toggle("tree-search-hit", hit)
      if (hit) this.revealAncestors(li)
    })
  }

  revealAncestors(li) {
    let branch = li.closest("[data-tree-branch]")
    while (branch) {
      branch.classList.remove("d-none")
      const parentLi = this.rowForBranch(branch)
      if (parentLi) this.syncIcons(parentLi, false)
      branch = branch.parentElement.closest("[data-tree-branch]")
    }
  }

  // ── Persistence ───────────────────────────────────────────
  persist() {
    if (!this.storageKey) return
    const collapsed = this.branches()
      .filter(b => b.classList.contains("d-none"))
      .map(b => b.dataset.treeBranch)
      .filter(Boolean)
    try { localStorage.setItem(this.storageKey, JSON.stringify(collapsed)) } catch (_) {}
  }

  restore() {
    if (!this.storageKey) return
    let collapsed = []
    try { collapsed = JSON.parse(localStorage.getItem(this.storageKey) || "[]") } catch (_) { return }
    if (!Array.isArray(collapsed) || !collapsed.length) return

    const set = new Set(collapsed.map(String))
    this.branches().forEach(branch => {
      if (!set.has(String(branch.dataset.treeBranch))) return
      branch.classList.add("d-none")
      const li = this.rowForBranch(branch)
      if (li) this.syncIcons(li, true)
    })
  }

  // ── Scoped node collections ───────────────────────────────
  branches() { return Array.from(this.element.querySelectorAll("[data-tree-branch]")) }
  rows()     { return Array.from(this.element.querySelectorAll("li[data-tree-row]")) }
}
